import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useAnswer, useLanguage, useMatchRound } from '../../api/hooks'
import type { MatchPair } from '../../api/types'
import { Button, Card, ErrorText, Spinner } from '../../components/ui'
import { sfx } from '../../lib/sound'

/** Seconds added to the clock as a penalty for a wrong pairing. */
const WRONG_PENALTY = 3
const START_SECONDS = 0 // counts up; the score is elapsed time

interface Tile {
  key: string
  entryId: number
  text: string
  side: 'source' | 'target'
}

function buildTiles(pairs: MatchPair[]): Tile[] {
  const tiles: Tile[] = pairs.flatMap((pair) => [
    { key: `s${pair.entry_id}`, entryId: pair.entry_id, text: pair.source_text, side: 'source' as const },
    { key: `t${pair.entry_id}`, entryId: pair.entry_id, text: pair.target_text, side: 'target' as const },
  ])
  // Fisher-Yates rather than sort(() => Math.random() - 0.5), which is biased
  // and on some engines does not shuffle at all.
  for (let i = tiles.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[tiles[i], tiles[j]] = [tiles[j], tiles[i]]
  }
  return tiles
}

export default function MatchGame() {
  const languageId = Number(useParams().id)
  const { data: language } = useLanguage(languageId)
  const { data: round, isPending, error, refetch } = useMatchRound(languageId, 6)
  const answer = useAnswer()

  const [tiles, setTiles] = useState<Tile[]>([])
  const [selected, setSelected] = useState<Tile | null>(null)
  const [matched, setMatched] = useState<Set<number>>(new Set())
  const [wrongKeys, setWrongKeys] = useState<string[]>([])
  const [penalty, setPenalty] = useState(0)
  const [mistakes, setMistakes] = useState(0)
  const [elapsed, setElapsed] = useState(START_SECONDS)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (round) {
      setTiles(buildTiles(round.pairs))
      setSelected(null)
      setMatched(new Set())
      setPenalty(0)
      setMistakes(0)
      setElapsed(0)
      setDone(false)
    }
  }, [round])

  // The clock. Stops the moment the last pair lands.
  useEffect(() => {
    if (done || tiles.length === 0) return
    const id = setInterval(() => setElapsed((s) => s + 0.1), 100)
    return () => clearInterval(id)
  }, [done, tiles.length])

  const totalPairs = round?.pairs.length ?? 0

  const grade = useCallback(
    (entryId: number, correct: boolean) => {
      // Matching is recognition, so it counts as a real review -- graded 4 for
      // a clean match and 2 for a fumbled one, which schedules it sooner.
      answer.mutate({
        entry_id: entryId,
        direction: 'recognize',
        grade: correct ? 4 : 2,
      })
    },
    [answer],
  )

  function tap(tile: Tile) {
    if (done || matched.has(tile.entryId)) return

    if (!selected) {
      sfx.tick()
      setSelected(tile)
      return
    }

    if (selected.key === tile.key) {
      setSelected(null)
      return
    }

    // Two tiles of the same side cannot pair; treat the second as a new pick.
    if (selected.side === tile.side) {
      setSelected(tile)
      sfx.tick()
      return
    }

    if (selected.entryId === tile.entryId) {
      sfx.pop()
      const next = new Set(matched).add(tile.entryId)
      setMatched(next)
      setSelected(null)
      grade(tile.entryId, true)

      if (next.size === totalPairs) {
        setDone(true)
        sfx.complete()
      }
    } else {
      sfx.wrong()
      setMistakes((m) => m + 1)
      setPenalty((p) => p + WRONG_PENALTY)
      grade(selected.entryId, false)

      // Flash both tiles red briefly, then clear.
      const keys = [selected.key, tile.key]
      setWrongKeys(keys)
      setSelected(null)
      setTimeout(() => setWrongKeys([]), 400)
    }
  }

  const finalTime = useMemo(() => elapsed + penalty, [elapsed, penalty])

  if (isPending) return <Spinner label="Dealing the cards" />
  if (error) {
    return (
      <div className="mx-auto max-w-md space-y-4 text-center">
        <ErrorText error={error} />
        <Link to={`/languages/${languageId}/games`}>
          <Button variant="secondary">Back to games</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div className="flex items-center justify-between text-sm">
        <Link
          to={`/languages/${languageId}/games`}
          className="text-muted hover:text-ink"
        >
          ✕ Quit
        </Link>
        <div className="flex items-center gap-4">
          <span className="tabular-nums">
            {matched.size} / {totalPairs}
          </span>
          <span className="font-mono tabular-nums text-brand">
            {finalTime.toFixed(1)}s
          </span>
        </div>
      </div>

      {done ? (
        <Card className="space-y-4 text-center">
          <div className="text-5xl">🔗</div>
          <h1 className="text-2xl font-semibold">All matched</h1>
          <p className="text-4xl font-semibold tabular-nums">
            {finalTime.toFixed(1)}s
          </p>
          <p className="text-sm text-muted">
            {mistakes === 0
              ? 'No mistakes at all.'
              : `${mistakes} wrong ${mistakes === 1 ? 'pair' : 'pairs'} · +${penalty}s penalty`}
          </p>
          <div className="flex justify-center gap-2">
            <Button onClick={() => refetch()}>Play again</Button>
            <Link to={`/languages/${languageId}/games`}>
              <Button variant="secondary">Done</Button>
            </Link>
          </div>
        </Card>
      ) : (
        <>
          <p className="text-center text-sm text-muted">
            Tap a {language?.source_language ?? 'English'} word, then its match.
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {tiles.map((tile) => {
              const isMatched = matched.has(tile.entryId)
              const isSelected = selected?.key === tile.key
              const isWrong = wrongKeys.includes(tile.key)

              return (
                <button
                  key={tile.key}
                  onClick={() => tap(tile)}
                  disabled={isMatched}
                  className={`min-h-16 rounded-xl border px-3 py-3 text-sm font-medium transition
                    ${
                      isMatched
                        ? 'border-transparent bg-positive/10 text-positive opacity-50'
                        : isWrong
                          ? 'border-negative bg-negative/10 text-negative'
                          : isSelected
                            ? 'border-brand bg-brand-soft text-brand'
                            : 'border-border-subtle bg-surface hover:border-brand'
                    }`}
                >
                  {tile.text}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
